/**
 * ============================================================================
 * AuditsTable — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: list audits scheduled for / completed against the supplier. Shows
 *          status, outcome, and auditor. When the server flags an SoD
 *          conflict (the auditor sat on the supplier qualification team),
 *          we render a clearly-visible badge — colour + text so it scans
 *          at a glance and through SR.
 *
 * SoD CALL-OUT: SUP-SoD-001 requires that the auditor be independent of the
 *  supplier qualification chain. We surface the violation here; the server
 *  decides what enforcement looks like.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './SupplierDetail.module.css';
import type { AuditRow, AuditStatus, AuditOutcome } from './useSupplierDetailQuery';

const STATUS_CLASS: Record<AuditStatus, string> = {
  PLANNED: styles.lifePending,
  IN_PROGRESS: styles.lifeRestricted,
  COMPLETED: styles.lifeActive,
  CANCELLED: styles.lifePending,
};

const OUTCOME_CLASS: Record<AuditOutcome, string> = {
  PASS: styles.critLow,
  PASS_WITH_FINDINGS: styles.critMajor,
  FAIL: styles.critCritical,
};

export interface AuditsTableProps {
  rows: AuditRow[];
}

export function AuditsTable({ rows }: AuditsTableProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  if (rows.length === 0) {
    return (
      <p className={styles.tabEmpty}>
        {t('supplier.detail.audits.empty', {
          defaultValue: 'No audits scheduled or completed yet.',
        })}
      </p>
    );
  }

  return (
    <table
      className={wstyles.table}
      aria-label={t('supplier.detail.audits.tableAria', {
        defaultValue: 'Supplier audits',
      })}
    >
      <caption className={styles.srOnly}>
        {t('supplier.detail.audits.tableCaption', {
          defaultValue:
            'List of supplier audits — scheduled date, completion, status, outcome, and auditor. Includes SoD warnings.',
        })}
      </caption>
      <thead>
        <tr>
          <th scope="col">
            {t('supplier.detail.audits.columns.scheduled', {
              defaultValue: 'Scheduled',
            })}
          </th>
          <th scope="col">
            {t('supplier.detail.audits.columns.completed', {
              defaultValue: 'Completed',
            })}
          </th>
          <th scope="col">
            {t('supplier.detail.audits.columns.status', {
              defaultValue: 'Status',
            })}
          </th>
          <th scope="col">
            {t('supplier.detail.audits.columns.outcome', {
              defaultValue: 'Outcome',
            })}
          </th>
          <th scope="col">
            {t('supplier.detail.audits.columns.auditor', {
              defaultValue: 'Auditor',
            })}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((a) => (
          <tr key={a.audit_id}>
            <td>
              <time dateTime={a.scheduled_for_utc}>
                {dateFmt.format(new Date(a.scheduled_for_utc))}
              </time>
            </td>
            <td>
              {a.completed_at_utc ? (
                <time dateTime={a.completed_at_utc}>
                  {dateFmt.format(new Date(a.completed_at_utc))}
                </time>
              ) : (
                <span className={styles.subdued}>—</span>
              )}
            </td>
            <td>
              <span
                className={`${styles.chip} ${STATUS_CLASS[a.status]}`}
                aria-label={t('supplier.detail.audits.statusAria', {
                  defaultValue: 'Status: {{s}}',
                  s: t(`supplier.detail.audits.status.${a.status}`, {
                    defaultValue: a.status,
                  }),
                })}
              >
                {t(`supplier.detail.audits.status.${a.status}`, {
                  defaultValue: a.status,
                })}
              </span>
            </td>
            <td>
              {a.outcome ? (
                <span
                  className={`${styles.chip} ${OUTCOME_CLASS[a.outcome]}`}
                  aria-label={t('supplier.detail.audits.outcomeAria', {
                    defaultValue: 'Outcome: {{o}}',
                    o: t(`supplier.detail.audits.outcome.${a.outcome}`, {
                      defaultValue: a.outcome,
                    }),
                  })}
                >
                  {t(`supplier.detail.audits.outcome.${a.outcome}`, {
                    defaultValue: a.outcome,
                  })}
                </span>
              ) : (
                <span className={styles.subdued}>—</span>
              )}
            </td>
            <td>
              <span>{a.auditor}</span>
              {a.has_sod_conflict && (
                <span
                  className={styles.sodBadge}
                  role="note"
                  aria-label={t('supplier.detail.audits.sodAria', {
                    defaultValue:
                      'Segregation of duties violation: this auditor is on the supplier qualification chain (SUP-SoD-001).',
                  })}
                >
                  {t('supplier.detail.audits.sod', {
                    defaultValue: 'SoD conflict',
                  })}
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
